"use client";

import React, { useId } from "react";
import { FIELD } from "@/components/creatorPortal/one/chrome";
import { Label } from "@/components/ui/design-system/label";
import { Rail } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";

/**
 * Shared pieces for the public /join pages: the caption the landing page uses
 * and the labelled field every application step is built from.
 */

/** Mono uppercase kicker in the brand colour: the DS rail role. */
export function Caption({ children }: { children: React.ReactNode }) {
  return <Rail className="text-nf-ds-brand">{children}</Rail>;
}

/**
 * An error replaces the hint rather than stacking under it: the hint has
 * already been read by the time a field is wrong, and two lines of small text
 * under an input is where people stop reading.
 *
 * The label is wired to the control with a generated id rather than relying on
 * visual proximity, and the hint/error is wired as its description — on a
 * public form the whole point is that someone tabbing through with a screen
 * reader hears what each box is for and what went wrong. The control is cloned
 * to receive them, so callers just nest their `Input`/`Textarea`/`Select`
 * normally; anything that isn't a single element is rendered untouched.
 */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  const id = useId();
  const messageId = `${id}-message`;
  const hasMessage = Boolean(error ?? hint);

  const element = React.isValidElement(children)
    ? (children as React.ReactElement<{
        id?: string;
        className?: string;
        "aria-invalid"?: boolean;
        "aria-describedby"?: string;
      }>)
    : null;
  const control = element
    ? React.cloneElement(element, {
        id,
        // The portal's field: `Input` and `Textarea` fill with the shadcn
        // `bg-background`, which resolves at `:root` and ignores the
        // `ds-next` layer these pages wear. See `FIELD` in `one/chrome`.
        className: cn(element.props.className, FIELD),
        ...(error ? { "aria-invalid": true } : {}),
        ...(hasMessage ? { "aria-describedby": messageId } : {}),
      })
    : children;

  return (
    <div className="flex flex-col gap-nf-ds-2">
      <Label htmlFor={id}>{label}</Label>
      {control}
      {error ? (
        // role="alert" so the message is announced when it appears. Errors
        // here only surface after a submit attempt, which is exactly the
        // moment a screen-reader user has no other signal that anything
        // changed further up the page.
        <p
          id={messageId}
          role="alert"
          className="font-nf-ds-sans text-nf-ds-hint text-nf-ds-destructive"
        >
          {error}
        </p>
      ) : hint ? (
        <p
          id={messageId}
          className="font-nf-ds-sans text-nf-ds-hint text-nf-ds-fg-muted"
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p
      role="alert"
      className="mt-nf-ds-3 font-nf-ds-sans text-nf-ds-hint text-nf-ds-destructive"
    >
      {message}
    </p>
  );
}
